import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLock, FaArrowLeft, FaUserCircle, FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

export default function ResetPassword() {
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [strength, setStrength] = useState(0);
  const [valids, setValids] = useState({ len: false, upper: false, num: false, sym: false });
  const secureOk = valids.len && valids.upper && valids.num && valids.sym;

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (data && !data.session) {
        const hash = window.location.hash || '';
        const qs = new URLSearchParams(hash.replace(/^#/, ''));
        const at = qs.get('access_token');
        const rt = qs.get('refresh_token');
        if (at && rt) {
          try {
            await supabase.auth.setSession({ access_token: at, refresh_token: rt });
          } catch {}
        }
      }
    })();
  }, []);

  const assess = (s) => {
    const v = {
      len: s.length >= 8,
      upper: /[A-Z]/.test(s),
      num: /[0-9]/.test(s),
      sym: /[^A-Za-z0-9]/.test(s)
    };
    setValids(v);
    setStrength(Object.values(v).filter(Boolean).length);
  };

  useEffect(() => { assess(password); }, [password]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!password || password.length < 8) { setError('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) { setError(err.message || 'No se pudo actualizar la contraseña'); return; }
    setSuccess('Contraseña actualizada correctamente');
    setTimeout(() => nav('/login'), 1200);
  };

  return (
    <div className="login-page-container">
      <div className="bg-bubbles">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <motion.div
        className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/20"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ zIndex: 10 }}
      >
        <Link 
          to="/login" 
          className="absolute -top-12 left-0 flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20"
        >
          <FaArrowLeft className="text-sm" />
          <span className="text-sm font-medium">Volver</span>
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg shadow-orange-500/50 mb-4">
            <FaUserCircle size={48} color="#FFFFFF" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-2">
            Commit<span className="text-orange-600">Flow</span>
          </h1>
          <p className="text-slate-600 font-medium">Establecer nueva contraseña</p>
          <p className="text-slate-500 text-sm mt-1">Ingresa tu nueva contraseña para tu cuenta</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm font-medium mb-4">{error}</div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-700 text-sm font-medium mb-4">{success}</div>
        )}

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-semibold text-slate-700">Nueva contraseña</label>
          <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="text-slate-400" />
              </div>
              <input
                type={show1 ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
              className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <button type="button" onClick={() => setShow1(v => !v)} className="absolute right-3 inset-y-0 my-auto h-8 w-8 flex items-center justify-center text-slate-500">{show1 ? <FaEyeSlash /> : <FaEye />}</button>
            </div>
            <div className="mt-2">
              <div className="h-2 rounded-full" style={{ background: '#e2e8f0' }}>
                <div className="h-2 rounded-full transition-all" style={{ width: `${(strength/4)*100}%`, background: strength < 2 ? '#ef4444' : strength < 3 ? '#f59e0b' : '#22c55e' }} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                <div className={`${valids.len ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.len ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.len ? <FaCheckCircle /> : <FaTimesCircle />}</span> Mínimo 8 caracteres</div>
                <div className={`${valids.upper ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.upper ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.upper ? <FaCheckCircle /> : <FaTimesCircle />}</span> Mayúscula</div>
                <div className={`${valids.num ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.num ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.num ? <FaCheckCircle /> : <FaTimesCircle />}</span> Número</div>
                <div className={`${valids.sym ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.sym ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.sym ? <FaCheckCircle /> : <FaTimesCircle />}</span> Símbolo</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="confirm" className="block text-sm font-semibold text-slate-700">Confirmar contraseña</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="text-slate-400" />
              </div>
              <input
                type={show2 ? 'text' : 'password'}
                id="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <button type="button" onClick={() => setShow2(v => !v)} className="absolute right-3 inset-y-0 my-auto h-8 w-8 flex items-center justify-center text-slate-500">{show2 ? <FaEyeSlash /> : <FaEye />}</button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !secureOk || password !== confirm}
            className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg shadow-orange-500/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? 'Actualizando...' : 'Guardar nueva contraseña'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}